import type { ReactNode } from 'react'

import type { BillingInterval, CatalogPlan } from '~/features/billing/types'

import { Button } from '~/elements/Button.tsx'
import { Divider } from '~/elements/Divider.tsx'
import { Skeleton } from '~/elements/Skeleton.tsx'
import { PlanBenefits } from '~/components/billing/PlanBenefits.tsx'
import { formatUsd, yearlyFreeMonths } from '~/features/billing/utils'
import { cn } from '~/lib/utils.ts'

// Placeholder card shown while the catalog loads, mirroring PlanCard's layout
// (title, price, CTA, divider, benefit rows) so the grid doesn't reflow.
export function PlanCardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'relative flex h-full flex-col items-start gap-5 rounded-2xl border border-secondary bg-fill p-5 shadow-lg',
        className
      )}
    >
      <Skeleton enabled className="h-6 w-24 rounded-md" />
      <Skeleton enabled className="h-9 w-28 rounded-md" />
      <Skeleton enabled className="h-10 w-full rounded-md" />
      <Divider className="-mx-5 w-[stretch]" />
      <div className="flex w-full flex-col gap-3">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton enabled className="h-4 w-3/4 rounded-md" key={index} />
        ))}
      </div>
    </div>
  )
}

export type PlanCardAction = {
  label: ReactNode
  onClick?: () => void
  disabled?: boolean
  loading?: boolean
  variant?: 'primary' | 'secondary'
}

function PlanPrice({ plan, interval }: { plan: CatalogPlan; interval: BillingInterval }) {
  if (!plan.price) {
    return (
      <div className="flex flex-col">
        <span className="font-mono text-3xl font-bold">Custom</span>
        <span className="text-sm text-content2">Annual contract</span>
      </div>
    )
  }

  if (plan.price.month === 0) {
    return (
      <div className="flex flex-col">
        <span className="font-mono text-3xl font-bold">{formatUsd(0)}</span>
        <span className="text-sm text-content2">Free forever</span>
      </div>
    )
  }

  if (interval === 'year') {
    const freeMonths = yearlyFreeMonths(plan)
    return (
      <div className="flex flex-col">
        <span className="font-mono text-3xl font-bold">
          {formatUsd(plan.price.year)}
          <span className="text-sm font-medium text-content2">/year</span>
        </span>
        <span className="text-sm text-content2">
          {formatUsd(Math.round((plan.price.year / 12) * 100) / 100)}/month billed yearly
          {freeMonths > 0 ? ` · ${freeMonths} months free` : ''}
        </span>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <span className="font-mono text-3xl font-bold">
        {formatUsd(plan.price.month)}
        <span className="text-sm font-medium text-content2">/month</span>
      </span>
      <span className="text-sm text-content2">or {formatUsd(plan.price.year)}/year</span>
    </div>
  )
}

export function PlanCard({
  plan,
  interval,
  current,
  recommended,
  action,
  note,
  className,
  ...props
}: TPolymorphicComponentProps<
  'div',
  {
    plan: CatalogPlan
    interval: BillingInterval
    current?: boolean
    recommended?: boolean
    action: PlanCardAction
    note?: ReactNode
  }
>) {
  return (
    <div
      className={cn(
        'relative flex h-full flex-col items-start gap-5 rounded-2xl border p-5 shadow-lg',
        recommended ? 'border-accent ring-1 ring-accent/30'
        : current ? 'border-content/40'
        : 'border-secondary',
        plan.id === 'free' ? 'bg-secondary' : 'bg-fill',
        className
      )}
      {...props}
    >
      <div className="flex w-full flex-col gap-1">
        <div className="flex items-center gap-2">
          <h2 className="text-bold text-lg font-bold">{plan.name}</h2>
          {current ?
            <span className="rounded-full border px-2.5 py-0.5 text-xs font-semibold text-content2">
              Current
            </span>
          : recommended ?
            <span className="rounded-full bg-accent px-2.5 py-0.5 text-xs font-semibold text-accent-contrast">
              Recommended
            </span>
          : null}
        </div>
        {plan.tagline && <p className="text-sm text-content3">{plan.tagline}</p>}
      </div>

      <PlanPrice interval={interval} plan={plan} />

      <div className="flex w-full flex-col gap-2">
        <Button
          className="w-full min-w-48 justify-center font-semibold"
          disabled={action.disabled}
          loading={action.loading}
          onClick={action.onClick}
          variant={action.variant ?? 'primary'}
        >
          {action.label}
        </Button>
        {note && <p className="text-xs text-content3">{note}</p>}
      </div>

      <Divider className="-mx-5 w-[stretch]" />

      <PlanBenefits plan={plan} />
    </div>
  )
}
